import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Breadcrumbs,
  Link
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteIcon from '@mui/icons-material/Delete';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import HomeIcon from '@mui/icons-material/Home';
import { versionApi, campaignApi } from '../services/api';
import VersionSelector from '../components/VersionSelector';

const VersionManagement = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [versionToCompare, setVersionToCompare] = useState(null);
  const [newVersionLabel, setNewVersionLabel] = useState('');
  const [newVersionDescription, setNewVersionDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const fetchVersions = useCallback(async () => {
    if (!campaignId) return;
    
    setLoading(true);
    try {
      const response = await versionApi.getVersions(campaignId);
      setVersions(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching versions:', err);
      setError('Failed to load versions: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        const response = await campaignApi.getCampaign(campaignId);
        setCampaign(response.data);
      } catch (err) {
        console.error('Error fetching campaign:', err);
        setError('Failed to load campaign details');
      }
    };
    
    if (campaignId) {
      fetchCampaign();
      fetchVersions();
    } else {
      setError('No campaign ID provided');
      setLoading(false);
    }
  }, [campaignId, fetchVersions]);
  

  
  const handleVersionChange = (version) => {
    setSelectedVersion(version);
  };
  
  const handleCreateVersion = async () => {
    if (!newVersionLabel.trim()) {
      setError('Version label is required');
      return;
    }
    
    setActionLoading(true);
    setError('');
    
    try {
      const response = await versionApi.createVersion({
        label: newVersionLabel,
        description: newVersionDescription,
        campaign_id: campaignId
      });
      
      setVersions([response.data, ...versions]);
      setNewVersionLabel('');
      setNewVersionDescription('');
      setCreateDialogOpen(false);
      setSuccessMessage('Version created successfully');
    } catch (err) {
      console.error('Error creating version:', err);
      setError('Failed to create version: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleRestoreVersion = async () => {
    if (!selectedVersion) return;
    
    setActionLoading(true);
    setError('');
    
    try {
      await versionApi.restoreVersion(selectedVersion.id);
      
      setRestoreDialogOpen(false);
      setSuccessMessage(`Version ${selectedVersion.label} restored successfully`);
      
      // Refresh versions after restore
      await fetchVersions();
      setSelectedVersion(null);
    } catch (err) {
      console.error('Error restoring version:', err);
      setError('Failed to restore version: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleDeleteVersion = async () => {
    if (!selectedVersion) return;
    
    setActionLoading(true);
    setError('');
    
    try {
      await versionApi.deleteVersion(selectedVersion.id);
      
      // Remove from versions list
      setVersions(versions.filter(v => v.id !== selectedVersion.id));
      setDeleteDialogOpen(false);
      setSelectedVersion(null);
      setSuccessMessage(`Version ${selectedVersion.label} deleted successfully`);
    } catch (err) {
      console.error('Error deleting version:', err);
      setError('Failed to delete version: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };
  
  return (
    <Container maxWidth="lg">
      <Paper elevation={2} sx={{ p: 4, mt: 4 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            href="/campaigns"
            onClick={(e) => {
              e.preventDefault();
              navigate('/campaigns');
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Campaigns
          </Link>
          {campaign && (
            <Typography color="text.primary">
              {campaign.campaign_name || `Campaign ${campaignId}`}
            </Typography>
          )}
          <Typography color="text.primary">Versions</Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            {campaign ? `Versions: ${campaign.campaign_name}` : 'Version Management'}
          </Typography>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            disabled={!campaignId}
          >
            Create Version
          </Button>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        ) : (
          <>
            {successMessage && (
              <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
                {successMessage}
              </Alert>
            )}
            
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Select a Version
              </Typography>
              {versions.length > 0 ? (
                <VersionSelector
                  versions={versions}
                  selectedVersion={selectedVersion}
                  onVersionChange={handleVersionChange}
                  onRestoreVersion={() => setRestoreDialogOpen(true)}
                />
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No versions found for this campaign. Create a new version to get started.
                </Alert>
              )}
            </Box>
            
            {selectedVersion && (
              <Card variant="outlined" sx={{ mb: 4 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" component="div">
                      {selectedVersion.label}
                      <Chip
                        label={selectedVersion.status === 'inactive' ? 'Inactive' : 'Active'}
                        color={selectedVersion.status === 'inactive' ? 'default' : 'success'}
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    </Typography>
                    
                    <Box>
                      <Tooltip title="Compare">
                        <IconButton 
                          onClick={() => setCompareDialogOpen(true)} 
                          size="small"
                        >
                          <CompareArrowsIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Created by: {selectedVersion.created_by_username || 'User'} on {formatDate(selectedVersion.created_at)}
                  </Typography>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="body1">
                    {selectedVersion.description || 'No description provided'}
                  </Typography>
                </CardContent>
                
                <CardActions>
                  <Button 
                    startIcon={<RestoreIcon />} 
                    color="warning"
                    onClick={() => setRestoreDialogOpen(true)}
                  >
                    Restore Version
                  </Button>
                  <Button 
                    startIcon={<DeleteIcon />} 
                    color="error"
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    Delete Version
                  </Button>
                </CardActions>
              </Card>
            )}
            
            <Typography variant="h6" gutterBottom>
              Available Versions
            </Typography>
            
            <Grid container spacing={3}>
              {versions.map((version) => (
                <Grid item xs={12} sm={6} md={4} key={version.id}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      cursor: 'pointer',
                      bgcolor: selectedVersion?.id === version.id ? 'action.selected' : 'inherit'
                    }}
                    onClick={() => handleVersionChange(version)}
                  >
                    <CardContent>
                      <Typography variant="h6" component="div" sx={{ mb: 1 }}>
                        {version.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {formatDate(version.created_at)}
                      </Typography>
                      <Typography variant="body2" noWrap>
                        {version.description || 'No description'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
              
              {versions.length === 0 && (
                <Grid item xs={12}>
                  <Typography variant="body1" align="center" sx={{ py: 4 }}>
                    No versions available
                  </Typography>
                </Grid>
              )}
            </Grid>
          </>
        )}
      </Paper>
      
      {/* Create Version Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <DialogTitle>Create New Version</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Create a snapshot of the current data state. This will allow you to restore to this point in the future.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="label"
            label="Version Label"
            fullWidth
            value={newVersionLabel}
            onChange={(e) => setNewVersionLabel(e.target.value)}
            required
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            id="description"
            label="Description (Optional)"
            fullWidth
            multiline
            rows={3}
            value={newVersionDescription}
            onChange={(e) => setNewVersionDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateVersion} 
            variant="contained"
            disabled={actionLoading || !newVersionLabel.trim()}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Restore Version Dialog */}
      <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)}>
        <DialogTitle>Restore Version</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to restore to version <strong>{selectedVersion?.label}</strong>? 
            This will replace all current data with the data from this version.
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleRestoreVersion} 
            color="warning" 
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Restore'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Version Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Version</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete version <strong>{selectedVersion?.label}</strong>? 
            This will permanently remove this version from the system.
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteVersion} 
            color="error" 
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Compare Versions Dialog */}
      <Dialog 
        open={compareDialogOpen} 
        onClose={() => setCompareDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Compare Versions</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Compare <strong>{selectedVersion?.label}</strong> with another version:
          </DialogContentText>
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="compare-version-label">Select Version to Compare</InputLabel>
            <Select
              labelId="compare-version-label"
              value={versionToCompare?.id || ''}
              label="Select Version to Compare"
              onChange={(e) => {
                const versionId = e.target.value;
                const version = versions.find(v => v.id === versionId) || null;
                setVersionToCompare(version);
              }}
            >
              <MenuItem value="">
                <em>Current (Latest)</em>
              </MenuItem>
              {versions.filter(v => v.id !== selectedVersion?.id).map((version) => (
                <MenuItem key={version.id} value={version.id}>
                  {version.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {versionToCompare && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Comparison results will be shown here
              </Typography>
              <Alert severity="info">
                This feature would show detailed differences between versions, such as changed 
                campaign budgets, modified accruals, and other data comparisons.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompareDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default VersionManagement; 